import React from 'react';
import { IconButton, MenuItem, Menu, ListItemIcon, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import {
  usePopupState,
  bindTrigger,
  bindMenu,
  bindHover,
} from 'material-ui-popup-state/hooks'
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import Icon from 'components/icon';
import { ChevronRight } from '@mui/icons-material';
import { isUsingTouchInput } from 'utils/app-context';



const createMenuItemFactory = (item, popupState, menuClasses) => {
  const {
    iconType, 
    isDefaultItem, 
    title, 
    submenuItems, 
    disabled,
    disabledReason, 
    tooltip,
    ...menuProps
  } = item;
  
  if (!title) {
    return null;
  } else if (submenuItems) {
    return (
      <SubMenu
        key={title}
        popupId={`${title}-submenu`}
        title={title}
        iconType={item.iconType}
        className={`${menuClasses.menuItem} ${isDefaultItem ? menuClasses.defaultItem : ''}`}
        disabled={disabled}
        disabledReason={disabledReason}
        tooltip={tooltip}
      >
        {submenuItems.map((i) => createMenuItemFactory(i, popupState, menuClasses)) }
      </SubMenu>
    );
  } else {
    return (
      <Tooltip 
        arrow 
        title={(disabled && disabledReason) 
          ? disabledReason 
          : (tooltip || '')} 
        key={title}
        placement='left'
      >
        <div>
          <MenuItem
            {...menuProps}
            key={title}
            aria-label={title}
            className={`${menuClasses.menuItem} ${isDefaultItem ? menuClasses.defaultItem : ''}`}
            disabled={disabled}
            onClick={() => {
              if (item.onSelect) {
                item.onSelect();
              }
              popupState.close()
            }}
          >
            <ListItemIcon className={menuClasses.icon}>
              <Icon type={iconType} />
            </ListItemIcon>
            { title }
          </MenuItem>
        </div>
      </Tooltip>
    );
  }
}


const submenuStyles = theme => ({
  menu: {
    marginTop: theme.spacing(-1),
  },
  title: {
    flexGrow: 1,
  },
  moreArrow: {
    marginRight: theme.spacing(-1),
    marginTop: theme.spacing(0.25)
  },
  icon: {
    minWidth: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
});


const SubMenu = withStyles(submenuStyles) (
  React.forwardRef(({
    classes, 
    title, 
    iconType, 
    popupId, 
    children, 
    className,
    disabled, 
    disabledReason, 
    tooltip,
    ...props 
  }, ref) => {
    const popupState = usePopupState({
      popupId,
      variant: 'popover',
    });

    const isMobileDevice = isUsingTouchInput();
    let triggerPopupProps = bindHover(popupState); // default trigger of submenu on hover (onMouseOver event)

    if (isMobileDevice) {
      // Trigger submenu to open on click
      const { onMouseLeave } = bindHover(popupState);
      triggerPopupProps = { ...bindTrigger(popupState), onMouseLeave };
    }

    return (
      <>
        <Tooltip
          arrow 
          title={(disabled && disabledReason) 
            ? disabledReason 
            : (tooltip || '')} 
          key={title}
          placement='left'
        >
          <div>
            <MenuItem
              {...triggerPopupProps}
              selected={popupState.isOpen}
              ref={ref}
              className={className}
              aria-label={title}
              disabled={disabled}
            >
              <ListItemIcon className={classes.icon}>
                <Icon type={iconType} />
              </ListItemIcon>
              <span className={classes.title}>{title}</span>
              <ChevronRight fontSize="small" className={classes.moreArrow} />
            </MenuItem>
          </div>
        </Tooltip>

        <HoverMenu
          {...bindMenu(popupState)}
          classes={{ paper: classes.menu }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          {...props}
        >
          {children}
        </HoverMenu>
      </>
    );
  })
);



const menuStyles = makeStyles(theme => ({
  defaultItem: {
    fontWeight: theme.typography.fontWeightBold,
  },
  icon: {
    minWidth: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
}));


const ContextMenu = ({ menuItems, ...props }) => {
  const menuClasses = menuStyles();
  const popupState = usePopupState({ popupId: 'context-menu', variant: 'popover' });

  return (
    <div>
      <IconButton
        aria-label={props['aria-label']}
        aria-controls="context-menu"
        variant="contained"
        {...bindTrigger(popupState)}
        {...props}
        size="large">
        <Icon type='menu' />
      </IconButton>

      {menuItems && menuItems.length > 0 &&
      <>
        <Menu
          {...bindMenu(popupState)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuItems.map((item) => {
            return createMenuItemFactory(item, popupState, menuClasses);
          })}
        </Menu>
      </>}
    </div>
  );
}

export default ContextMenu;